import { Injectable, isDevMode } from '@angular/core';
import { DeviceResult, InverterRegister, ModbusLink, RegisterTable, ok } from 'ngx-modbus';
import { ModeSection, FieldDefinition } from '../types/config-form.types';
import { applyWriteTransform, bitToggleToRegister, bitSelectToRegister } from '../transforms/field-transforms';
import { createDebugLogger, DebugConfig } from '../../shared/debug';

const BATCH_WRITER_DEBUG: DebugConfig = { ENABLED: false, LOG_DATA: false, LOG_PERFORMANCE: false };
const log = createDebugLogger('[BatchWriter]', BATCH_WRITER_DEBUG);

export interface WriteRequest {
  key: string;
  value: number;
}

export interface BatchWriteConfig {
  section: ModeSection;
  values: Record<string, any>;
  fields: FieldDefinition[];
  /** ModbusLink command instance - used to check validity before writing */
  command?: ModbusLink;
  /** Register table — used for bit-field accuracy validation */
  registerTable?: Partial<RegisterTable>;
}

/**
 * Service to handle batch Modbus writes
 * Transforms field values and builds write requests
 */
@Injectable({ providedIn: 'root' })
export class BatchWriterService {
  /**
   * Build write requests from form values
   * Applies transforms and adds control register if configured
   */
  buildWriteRequests(config: BatchWriteConfig): WriteRequest[] {
    const { section, values, fields, command } = config;
    const requests: WriteRequest[] = [];

    // Phase 1: collect bit-field mutations grouped by parent register
    const bitGroups = new Map<string, { regValue: number; fields: FieldDefinition[] }>();
    const bitFieldKeys = new Set<string>();

    for (const field of fields) {
      if (command?.validity?.[field.registerKey] === false) continue;
      const isBitField = field.type === 'bit-toggle' || field.type === 'bit-select';
      if (!isBitField) continue;

      const parentKey = field.bitRegisterKey ?? field.registerKey;
      bitFieldKeys.add(field.key);

      if (!bitGroups.has(parentKey)) {
        // Start from current device value (command.data) for accurate merging.
        // INVARIANT: bit-config registers must have accuracy=1, otherwise bit manipulation
        // on accuracy-scaled values produces incorrect results.
        const currentValue = (command?.data?.[parentKey] as number) ?? 0;
        bitGroups.set(parentKey, { regValue: currentValue, fields: [] });

        if (isDevMode() && config.registerTable) {
          const parentReg = config.registerTable[parentKey] as InverterRegister | undefined;
          if (parentReg?.accuracy != null && parentReg.accuracy !== 1) {
            console.warn(
              `[BatchWriter] bit-field "${field.key}" uses parent "${parentKey}" with accuracy=${parentReg.accuracy}. ` +
              `Bit manipulation on accuracy-scaled values may produce incorrect results.`,
            );
          }
        }
      }
      bitGroups.get(parentKey)!.fields.push(field);
    }

    // Apply each bit mutation sequentially onto the parent register value
    for (const [parentKey, group] of bitGroups) {
      let merged = group.regValue;
      for (const field of group.fields) {
        const val = values[field.key];
        if (val === undefined || val === null) continue;

        if (field.type === 'bit-toggle' && field.bitIndex !== undefined) {
          merged = bitToggleToRegister(merged, !!val, field.bitIndex);
        } else if (field.type === 'bit-select' && field.bitRange) {
          merged = bitSelectToRegister(merged, Number(val), field.bitRange[0], field.bitRange[1]);
        }
      }
      requests.push({ key: parentKey, value: merged });
    }

    // Phase 2: normal (non-bit) fields
    for (const field of fields) {
      if (bitFieldKeys.has(field.key)) continue;
      if (command?.validity?.[field.registerKey] === false) continue;

      const value = values[field.key];
      if (value === undefined || value === null) continue;

      const transformedValue = applyWriteTransform(value, {
        type: field.transform ?? 'none',
        factor: field.transformFactor,
      });

      if (transformedValue !== null) {
        requests.push({
          key: field.registerKey,
          value: transformedValue,
        });
      }
    }

    // Add control register at the end if configured
    if (section.controlRegister) {
      requests.push({
        key: section.controlRegister,
        value: 1,
      });
    }

    log.table('buildWriteRequests', requests.map(r => ({ key: r.key, value: r.value })));

    return requests;
  }

  /**
   * Execute batch write using the provided command object
   * @param command ModbusLink command instance
   * @param requests Write requests to execute
   * @param registerTable The register table to use
   * @param options Additional options (e.g., validityMask)
   */
  /**
   * Execute batch write using the safe Result-based API.
   * Returns DeviceResult<void> — never throws.
   */
  async executeBatchWrite(
    command: ModbusLink | undefined,
    requests: WriteRequest[],
    registerTable: Partial<RegisterTable>,
    options?: {
      useValidityMask?: string;
      controlRegister?: string;
      writeBlockSize?: number;
      updateProgress?: (percentage: number) => void;
    },
  ): Promise<DeviceResult<void>> {
    if (!command || requests.length === 0) {
      return ok(undefined);
    }

    const batchTimer = log.startTimer(`executeBatchWrite (${requests.length} requests)`);

    const verifySkipAddresses: number[] = [];
    const controlAddr = options?.controlRegister ? registerTable[options.controlRegister]?.address : undefined;
    if (controlAddr != null) {
      verifySkipAddresses.push(controlAddr);
    }

    const result = await command.executeWriteMultipleHoldingRegistersSafe(
      requests,
      registerTable,
      {
        ...options,
        verifyAfterWrite: true,
        verifySkipAddresses: verifySkipAddresses.length > 0
          ? verifySkipAddresses
          : undefined,
      },
    );

    log.endTimer(batchTimer, { requestCount: requests.length });
    return result;
  }
}
